import {useId} from "react";
import {useMessages} from "i18n";
import {VisuallyHidden} from "components/Accessibility.styles";
import {useCopyFeedback} from "./shareLink";
import {CopyHint, CopyLinkButton, CopyPill, LinkBox, ShareUrl} from "./SuccessCard.styles";

/**
 * The one-line share link that copies on press, shared by the hosted {@link SuccessCard}, the instant
 * {@link InstantLiveCard} and the file request's sent card (ui#2486).
 *
 * The URL is the copy button's accessible NAME and the copy instruction its DESCRIPTION (ui#3016). The
 * instruction used to be the name, as an `aria-label`, which replaced the button's text: the URL never
 * reached the accessibility tree, so a screen-reader user never heard the link, and a browsing agent,
 * whose clipboard is not the user's, had no way to read it and hand it back. The visible ellipsis is
 * only paint; the name carries the whole URL.
 *
 * The URL is painted BESIDE the button, never inside it, and the button takes its name by reference
 * (kitecorp/transferify#375): on a Meta site the pixel's automatic events send a clicked button's own text,
 * and this link is a secret, its path the transfer's capability and, for a Zero-Knowledge or live transfer,
 * its fragment the key. The button still covers the whole box ({@link CopyLinkButton}), so pressing anywhere
 * on the link copies it, as before.
 *
 * `persistentCopy` is the live card's box (kitecorp/transferify-ui#3320, P3): "Copy" is on show at all times
 * rather than on hover or focus, which a phone never has.
 */
export function ShareLinkBox({url, persistentCopy = false}: {url: string; persistentCopy?: boolean}) {
	const {sender, common} = useMessages();
	const {copied, copy} = useCopyFeedback(url);
	const urlId = useId();
	const hintId = useId();
	return (
		<LinkBox $withPill={persistentCopy}>
			<ShareUrl id={urlId}>{url}</ShareUrl>
			<CopyLinkButton type="button" onClick={copy} aria-labelledby={urlId} aria-describedby={hintId}
				$pill={persistentCopy}>
				<VisuallyHidden id={hintId}>{sender.success.copyLinkHint}</VisuallyHidden>
				{persistentCopy ? (
					<CopyPill $copied={copied} role="status" aria-live="polite">
						{copied ? sender.success.linkCopied : <span aria-hidden="true">{common.copy}</span>}
					</CopyPill>
				) : (
					<CopyHint $copied={copied} role="status" aria-live="polite">
						{copied ? sender.success.linkCopied : <span aria-hidden="true">{sender.success.clickToCopy}</span>}
					</CopyHint>
				)}
			</CopyLinkButton>
		</LinkBox>
	);
}
